import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ClubService } from "../../../services/clubService";
import { EventService } from "../../../services/eventService";
import { isClubLeaderRole } from "../../../utils/notificationTargeting";
import { attendingEventIds } from "../../../utils/rsvpCacheCore";
import { useAuth } from "./AuthContext";

/**
 * Web counterpart of contexts/DataContext.js.
 *
 * useData() ->
 *   userClubs                 clubs the signed-in user belongs to; each row carries `member_role`
 *   userClubsLoaded           true once a live load for the current uid has applied
 *   userClubsError            message when the last load failed (null otherwise); userClubsLoaded is also set so spinners stop
 *   leadsAnyClub              true when the user is admin / e-board of any club (shared isClubLeaderRole)
 *   loadUserClubs()           refetch; resolves to the fresh array (last good list on failure)
 *   patchUserClubMembership(clubId, patch)   merge into one cached row without a fetch
 *
 *   userEvents                the user's attendance rows from EventService.getUserEvents:
 *                             wrappers { id: <attendee doc id>, event_id, status, checked_in, event }
 *   userEventsLoaded          true once a live load for the current uid has applied (only then is
 *                             "not in goingEventIds" a real "not going")
 *   userEventsError           message when the last load failed (null otherwise)
 *   goingEventIds             Set of event ids with status "attending" (compared on event_id, never row id)
 *   checkedInEventIds         Set of event ids with checked_in === true
 *   loadUserEvents()          refetch; resolves to the fresh array (last good list on failure)
 *   patchUserEvent(eventId, rowOrNull)  optimistic write: replace every row for eventId with `row`
 *                             (null removes them). An older in-flight load can no longer overwrite it.
 *   eventsVersion             bumps on every event write the app knows about (RSVP, check-in, edit)
 *   bumpEventsVersion()       bump eventsVersion and reload userEvents (call after a successful write)
 * Loads when the user is signed in AND verified, clears on sign-out, refreshes when the tab regains focus.
 */
const DataContext = createContext(null);

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useData must be used within a DataProvider");
  return ctx;
}

const errText = (e, fallback) => e?.message || (typeof e === "string" ? e : "") || fallback;

export function DataProvider({ children }) {
  const { user } = useAuth();
  const uid = user?.uid;
  const verified = user?.emailVerified === true;

  const [userClubs, setUserClubs] = useState([]);
  const [userClubsLoaded, setUserClubsLoaded] = useState(false);
  const [userClubsError, setUserClubsError] = useState(null);
  const [userEvents, setUserEvents] = useState([]);
  const [userEventsLoaded, setUserEventsLoaded] = useState(false);
  const [userEventsError, setUserEventsError] = useState(null);
  const [eventsVersion, setEventsVersion] = useState(0);
  const uidRef = useRef(null);
  const reqRef = useRef(0); // newest-wins ordering for club loads; patches bump it too
  const clubsRef = useRef([]);
  const evReqRef = useRef(0); // newest-wins ordering for event loads; patches bump it too
  const eventsRef = useRef([]);
  uidRef.current = verified ? uid : null;
  clubsRef.current = userClubs;
  eventsRef.current = userEvents;

  const loadUserClubs = useCallback(async () => {
    const requestUid = uidRef.current;
    if (!requestUid) return clubsRef.current;
    const myReq = ++reqRef.current;
    try {
      const { data, error } = await ClubService.getUserClubs(requestUid);
      if (error) throw error;
      if (uidRef.current !== requestUid || reqRef.current !== myReq) return clubsRef.current; // account switched / superseded
      setUserClubs(data || []);
      setUserClubsError(null);
      setUserClubsLoaded(true);
      return data || [];
    } catch (e) {
      if (__DEV__) console.error("Error loading user clubs:", e);
      if (uidRef.current === requestUid && reqRef.current === myReq) { setUserClubsError(errText(e, "Failed to load your clubs")); setUserClubsLoaded(true); }
      return clubsRef.current;
    }
  }, []);

  const patchUserClubMembership = useCallback((clubId, patch) => {
    if (!clubId || !patch) return;
    reqRef.current += 1; // an older in-flight load must not overwrite this patch
    setUserClubs((prev) => {
      if (!prev.some((c) => c && c.id === clubId)) return prev;
      const next = prev.map((c) => (c && c.id === clubId ? { ...c, ...patch } : c));
      clubsRef.current = next;
      return next;
    });
  }, []);

  const loadUserEvents = useCallback(async () => {
    const requestUid = uidRef.current;
    if (!requestUid) return eventsRef.current;
    const myReq = ++evReqRef.current;
    try {
      const { data, error } = await EventService.getUserEvents(requestUid);
      if (error) throw error;
      if (uidRef.current !== requestUid || evReqRef.current !== myReq) return eventsRef.current; // account switched / superseded
      const rows = Array.isArray(data) ? data : [];
      setUserEvents(rows);
      setUserEventsError(null);
      setUserEventsLoaded(true);
      return rows;
    } catch (e) {
      if (__DEV__) console.error("Error loading user events:", e);
      if (uidRef.current === requestUid && evReqRef.current === myReq) setUserEventsError(errText(e, "Failed to load your events"));
      return eventsRef.current;
    }
  }, []);

  const patchUserEvent = useCallback((eventId, row) => {
    if (!eventId) return;
    evReqRef.current += 1; // an older in-flight load must not overwrite this optimistic row
    setUserEvents((prev) => {
      const rest = prev.filter((r) => !r || r.event_id !== eventId);
      const next = row ? [...rest, { ...row, event_id: eventId }] : rest;
      eventsRef.current = next;
      return next;
    });
  }, []);

  const bumpEventsVersion = useCallback(() => {
    setEventsVersion((v) => v + 1);
    loadUserEvents();
  }, [loadUserEvents]);

  useEffect(() => {
    if (uid && verified) {
      Promise.allSettled([loadUserClubs(), loadUserEvents()]);
    } else {
      reqRef.current += 1;
      evReqRef.current += 1;
      setUserClubs([]);
      setUserClubsLoaded(false);
      setUserClubsError(null);
      setUserEvents([]);
      setUserEventsLoaded(false);
      setUserEventsError(null);
    }
  }, [uid, verified, loadUserClubs, loadUserEvents]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && uidRef.current) Promise.allSettled([loadUserClubs(), loadUserEvents()]);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [loadUserClubs, loadUserEvents]);

  const leadsAnyClub = useMemo(() => userClubs.some((c) => isClubLeaderRole(c.member_role)), [userClubs]);
  const goingEventIds = useMemo(() => new Set(attendingEventIds(userEvents)), [userEvents]);
  const checkedInEventIds = useMemo(
    () => new Set(userEvents.filter((r) => r && r.checked_in === true && r.event_id).map((r) => r.event_id)),
    [userEvents]
  );

  const value = useMemo(
    () => ({
      userClubs, userClubsLoaded, userClubsError, leadsAnyClub, loadUserClubs, patchUserClubMembership,
      userEvents, userEventsLoaded, userEventsError, goingEventIds, checkedInEventIds,
      loadUserEvents, patchUserEvent, eventsVersion, bumpEventsVersion,
    }),
    [userClubs, userClubsLoaded, userClubsError, leadsAnyClub, loadUserClubs, patchUserClubMembership,
      userEvents, userEventsLoaded, userEventsError, goingEventIds, checkedInEventIds,
      loadUserEvents, patchUserEvent, eventsVersion, bumpEventsVersion]
  );
  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}
